import React, { useState, useEffect } from 'react';
import { 
  Upload, FileText, X, AlertCircle, CheckCircle, Info, Trash2, Search, ChevronRight, ChevronDown, Eye, Table
} from 'lucide-react';
import { apiCall, getApiUrl } from './Api';
import { UploadTypeSelector, VersionTimeline } from './VersionComponents';
import { getCurrentToken } from './SessionManager';

/**
 * EnhancedFilesTab Component - Merged version
 * Combines existing batch upload functionality with version management features
 * + File sample preview feature
 */
const EnhancedFilesTab = ({ session, files, onRefresh }) => {
  // Upload state - existing batch upload functionality
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadQueue, setUploadQueue] = useState([]);
  
  // Version upload state - new functionality from EnhancedFilesTab
  const [uploadMode, setUploadMode] = useState(null); // null | 'type-selection' | 'uploading-versioned' | 'success' | 'error'
  const [uploadConfig, setUploadConfig] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  
  // Classification state for version uploads
  const [fileDataIntent, setFileDataIntent] = useState('customer_profile');
  const [fileColumnRoles, setFileColumnRoles] = useState({});
  
  // UI state - existing functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [showVersionTimeline, setShowVersionTimeline] = useState(false);
  const [collapsedVersions, setCollapsedVersions] = useState(new Set());
  
  // File preview state
  const [previewFileId, setPreviewFileId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Constants
  const maxConcurrentUploads = 3;
  const isVersioned = session?.unified_at && session?.current_version > 0;

  // Toggle version collapse
  const toggleVersionCollapse = (version) => {
    setCollapsedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(version)) {
        newSet.delete(version);
      } else {
        newSet.add(version);
      }
      return newSet;
    });
  };

  // ==================== Utility Functions (existing) ====================
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // ==================== Initial Upload Functions (existing batch upload) ====================
  
  const uploadSingleFile = async (file) => {
    const token = getCurrentToken();
    
    try {
      setUploadProgress(prev => ({
        ...prev,
        [file.name]: { status: 'uploading', progress: 0, error: null }
      }));

      const formData = new FormData();
      formData.append('file', file);

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { ...prev[file.name], progress: Math.round(percentComplete) }
            }));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: 'completed', progress: 100, error: null }
            }));
            resolve();
          } else {
            const error = `Upload failed: ${xhr.statusText}`;
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: { status: 'error', progress: 0, error }
            }));
            reject(new Error(error));
          }
        });

        xhr.addEventListener('error', () => {
          const error = 'Network error during upload';
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', progress: 0, error }
          }));
          reject(new Error(error));
        });

        xhr.timeout = 30 * 60 * 1000; // 30 minutes
        xhr.addEventListener('timeout', () => {
          const error = 'Upload timed out';
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { status: 'error', progress: 0, error }
          }));
          reject(new Error(error));
        });

        xhr.open('POST', `${getApiUrl()}/api/cdp/sessions/${session.session_id}/upload`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });

    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      throw error;
    }
  };

  const uploadFilesInBatches = async (files) => {
    for (let i = 0; i < files.length; i += maxConcurrentUploads) {
      const batch = files.slice(i, i + maxConcurrentUploads);
      await Promise.all(batch.map(file => uploadSingleFile(file)));
    }
  };

  const handleInitialUpload = async (selectedFiles) => {
    // Validate file sizes
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB
    const oversizedFiles = selectedFiles.filter(f => f.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      alert(`The following files exceed 2GB limit:\n${oversizedFiles.map(f => f.name).join('\n')}`);
      return;
    }

    const initialProgress = {};
    selectedFiles.forEach(file => {
      initialProgress[file.name] = { status: 'queued', progress: 0, error: null };
    });
    setUploadProgress(initialProgress);
    setUploadQueue(selectedFiles);

    try {
      setUploading(true);
      await uploadFilesInBatches(selectedFiles);
      onRefresh();
    } catch (error) {
      console.error('Error in upload process:', error);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadProgress({});
        setUploadQueue([]);
      }, 5000);
    }
  };

  // ==================== Version Upload Functions (new from EnhancedFilesTab) ====================
  
  const handleVersionUploadProceed = async (config) => {
    setUploadConfig(config);
    setUploadMode('uploading-versioned');

    try {
      const formData = new FormData();
      uploadingFiles.forEach(file => formData.append('files', file));
      formData.append('upload_type', config.uploadType);
      if (config.campaignId) {
        formData.append('campaign_id', config.campaignId);
      }
      if (config.description) {
        formData.append('description', config.description);
      }
      // Classification fields
      if (config.dataIntent && config.dataIntent !== 'customer_profile') {
        formData.append('data_intent', config.dataIntent);
      }
      if (config.columnRolesJson) {
        formData.append('column_roles_json', config.columnRolesJson);
      }
      if (config.metricDefinitionsJson) {
        formData.append('metric_definitions_json', config.metricDefinitionsJson);
      }

      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/upload-subsequent`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUploadMode('success');
        setTimeout(() => {
          setUploadMode(null);
          setUploadingFiles([]);
          setUploadConfig(null);
          onRefresh();
        }, 2000);
      } else {
        setUploadMode('error');
      }
    } catch (error) {
      console.error('Error uploading versioned files:', error);
      setUploadMode('error');
    }
  };

  const handleCancelUpload = () => {
    setUploadMode(null);
    setUploadingFiles([]);
    setUploadConfig(null);
  };

  // ==================== File Selection Handler (smart routing) ====================
  
  const handleFilesSelected = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    if (selectedFiles.length === 0) return;
    
    // Smart routing based on version state
    if (isVersioned) {
      // Show version upload type selection
      setUploadingFiles(selectedFiles);
      setUploadMode('type-selection');
    } else {
      // Use existing batch upload for initial upload
      await handleInitialUpload(selectedFiles);
    }
  };

  // ==================== File Management (existing) ====================
  
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;

    try {
      const token = getCurrentToken();
      await apiCall(`/api/cdp/sessions/${session.session_id}/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  // ==================== File Preview ====================
  
  const handlePreviewFile = async (fileId) => {
    if (previewFileId === fileId) {
      // Toggle off
      setPreviewFileId(null);
      setPreviewData(null);
      return;
    }
    
    try {
      setPreviewFileId(fileId);
      setPreviewLoading(true);
      setPreviewData(null);
      
      const token = getCurrentToken();
      const response = await apiCall(
        `/api/cdp/sessions/${session.session_id}/files/${fileId}/preview?rows=10`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      } else {
        setPreviewData({ error: 'Failed to load preview' });
      }
    } catch (error) {
      console.error('Error loading file preview:', error);
      setPreviewData({ error: 'Failed to load preview' });
    } finally {
      setPreviewLoading(false);
    }
  };

  // ==================== Filtering (existing) ====================
  
  const filteredFiles = files.filter(file => {
    const matchesSearch = file.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const uploadDate = new Date(file.uploaded_at);
    const matchesDateFrom = !dateFilter.from || uploadDate >= new Date(dateFilter.from);
    const matchesDateTo = !dateFilter.to || uploadDate <= new Date(dateFilter.to);
    return matchesSearch && matchesDateFrom && matchesDateTo;
  });

  // Group files by version (from EnhancedFilesTab)
  const filesByVersion = filteredFiles.reduce((acc, file) => {
    const version = file.version || 1;
    if (!acc[version]) acc[version] = [];
    acc[version].push(file);
    return acc;
  }, {});

  const versions = Object.keys(filesByVersion).sort((a, b) => b - a);

  // ==================== Render ====================
  
  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Data Files</h2>
              <p className="text-sm text-gray-600 mt-1">
                {isVersioned 
                  ? `Current Version: ${session.current_version}` 
                  : 'Upload your customer data files'}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {isVersioned && (
                <button
                  onClick={() => setShowVersionTimeline(!showVersionTimeline)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                >
                  <span>Version History</span>
                </button>
              )}

              <label className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>{uploading ? 'Uploading...' : isVersioned ? 'Upload New Data' : 'Upload Files'}</span>
                <input
                  type="file"
                  multiple
                  accept=".csv,.xlsx,.xls,.txt"
                  onChange={handleFilesSelected}
                  className="hidden"
                  disabled={uploading || uploadMode !== null}
                />
              </label>
            </div>
          </div>
        </div>

        {/* Version Upload Type Selection */}
        {uploadMode === 'type-selection' && (
          <div className="p-6 border-b bg-gray-50 space-y-4">
            <UploadTypeSelector
              sessionId={session.session_id}
              onProceed={(config) => {
                // Inject classification fields into config before proceeding
                handleVersionUploadProceed({
                  ...config,
                  dataIntent: fileDataIntent,
                  columnRolesJson: fileDataIntent !== 'customer_profile' ? JSON.stringify(fileColumnRoles) : null,
                  metricDefinitionsJson: null,
                });
              }}
              onCancel={handleCancelUpload}
            />

            {/* Inline Data Classification */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Data Classification</h4>
              <p className="text-xs text-gray-500 mb-3">Classify this upload to enable automatic event extraction and campaign attribution.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Data Intent</label>
                  <select value={fileDataIntent} onChange={(e) => setFileDataIntent(e.target.value)}
                    className="w-full px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="customer_profile">Customer Profile (default)</option>
                    <option value="campaign_response">Campaign Response</option>
                    <option value="campaign_conversion">Campaign Conversion</option>
                    <option value="event_stream">Event Stream</option>
                    <option value="transaction">Transaction</option>
                    <option value="survey_feedback">Survey / Feedback</option>
                    <option value="enrichment">Enrichment</option>
                  </select>
                </div>
              </div>

              {fileDataIntent !== 'customer_profile' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Column Role Mapping (optional)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['customer_key', 'event_type', 'event_value', 'event_timestamp'].map(role => (
                      <div key={role}>
                        <label className="block text-[11px] text-gray-400 mb-0.5">{role.replace('_', ' ')}</label>
                        <input type="text" value={fileColumnRoles[role] || ''}
                          onChange={(e) => setFileColumnRoles(prev => ({ ...prev, [role]: e.target.value }))}
                          className="w-full px-2 py-1 border rounded text-xs"
                          placeholder="column name" />
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Enter the column names from your file that correspond to each role.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Version Upload Progress */}
        {/* Version Upload Status (consolidated: spinner / success / error in one block) */}
        {(uploadMode === 'uploading-versioned' || uploadMode === 'success' || uploadMode === 'error') && (
          <div className={`p-6 border-b ${
            uploadMode === 'success' ? 'bg-green-50' : uploadMode === 'error' ? 'bg-red-50' : 'bg-blue-50'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {uploadMode === 'uploading-versioned' && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                )}
                {uploadMode === 'success' && <CheckCircle className="h-6 w-6 text-green-600" />}
                {uploadMode === 'error' && <AlertCircle className="h-6 w-6 text-red-600" />}
                <div>
                  <p className={`font-medium ${
                    uploadMode === 'success' ? 'text-green-900' : uploadMode === 'error' ? 'text-red-900' : 'text-blue-900'
                  }`}>
                    {uploadMode === 'uploading-versioned' && `Uploading ${uploadingFiles.length} file${uploadingFiles.length > 1 ? 's' : ''}...`}
                    {uploadMode === 'success' && 'Upload Successful!'}
                    {uploadMode === 'error' && 'Upload Failed'}
                  </p>
                  <p className={`text-sm ${
                    uploadMode === 'success' ? 'text-green-700' : uploadMode === 'error' ? 'text-red-700' : 'text-blue-700'
                  }`}>
                    {uploadMode === 'uploading-versioned' && 'Processing files...'}
                    {uploadMode === 'success' && 'Files uploaded and ready for processing'}
                    {uploadMode === 'error' && 'Please try again'}
                  </p>
                </div>
              </div>
              {uploadMode === 'error' && (
                <button onClick={handleCancelUpload} className="text-red-400 hover:text-red-600">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Version Timeline */}
        {showVersionTimeline && (
          <div className="p-6 border-b bg-gray-50">
            <VersionTimeline sessionId={session.session_id} onRefresh={onRefresh} />
          </div>
        )}

        {/* Batch Upload Progress (consolidated with tips) */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="p-6 border-b">
            <h3 className="font-semibold text-gray-900 mb-3">Upload Progress</h3>
            <div className="space-y-3">
              {uploadQueue.map(file => {
                const progress = uploadProgress[file.name];
                if (!progress) return null;

                return (
                  <div key={file.name} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 truncate flex-1">
                        {file.name}
                      </span>
                      <span className="text-gray-500 ml-2">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            progress.status === 'completed' 
                              ? 'bg-green-500' 
                              : progress.status === 'error'
                              ? 'bg-red-500'
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 w-12 text-right">
                        {progress.progress}%
                      </span>
                    </div>

                    {progress.status === 'completed' && (
                      <div className="flex items-center text-xs text-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Upload complete
                      </div>
                    )}

                    {progress.status === 'error' && (
                      <div className="flex items-center text-xs text-red-600">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {progress.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {uploading && (
              <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex items-start space-x-1.5">
                <Info className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                <span>Keep this page open. Files are processed in batches of {maxConcurrentUploads}.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search and Filter Bar */}
      {files.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <input
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                  placeholder="From date"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg text-sm"
                  placeholder="To date"
                />
              </div>
            </div>
            
            {versions.length > 1 && (
              <div className="mt-3 flex items-center justify-end space-x-2">
                <button
                  onClick={() => setCollapsedVersions(new Set())}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setCollapsedVersions(new Set(versions))}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Collapse All
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Files Display */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {filteredFiles.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">
                {files.length === 0 ? 'No files uploaded yet' : 'No files match your filters'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {files.length === 0 && 'Upload CSV, Excel, or TXT files to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {versions.map(version => {
                const isCollapsed = collapsedVersions.has(version);
                const versionFiles = filesByVersion[version];
                
                return (
                  <div key={version}>
                    <div 
                      className="flex items-center justify-between mb-3 cursor-pointer group"
                      onClick={() => toggleVersionCollapse(version)}
                    >
                      <div className="flex items-center space-x-2">
                        {isCollapsed ? (
                          <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                        )}
                        <h3 className="font-medium text-gray-900">Version {version}</h3>
                        {version == session.current_version && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                            Current
                          </span>
                        )}
                        <span className="text-sm text-gray-500">
                          ({versionFiles.length} {versionFiles.length === 1 ? 'file' : 'files'})
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                      </div>
                    </div>

                    {!isCollapsed && (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Filename
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rows
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Columns
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Size
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Uploaded
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {versionFiles.map((file) => (
                              <React.Fragment key={file.file_id}>
                                <tr className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                    <div>
                                      <span className="text-sm font-medium text-gray-900">{file.filename}</span>
                                      {file.upload_type && file.upload_type !== 'initial' && (
                                        <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                          {file.upload_type.replace('_', ' ')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-600 uppercase">{file.file_type}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {(file.total_rows || 0).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {file.total_columns || 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {formatFileSize(file.file_size)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {new Date(file.uploaded_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                  <div className="flex items-center justify-end space-x-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePreviewFile(file.file_id);
                                      }}
                                      className={`p-1 rounded transition-colors ${
                                        previewFileId === file.file_id 
                                          ? 'text-blue-600 bg-blue-50' 
                                          : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                      }`}
                                      title="Preview sample data"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFile(file.file_id);
                                      }}
                                      disabled={session.columns_locked}
                                      className="p-1 text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title={session.columns_locked ? "Cannot delete - columns are locked" : "Delete file"}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              
                              {/* Expandable Preview Panel */}
                              {previewFileId === file.file_id && (
                                <tr>
                                  <td colSpan="7" className="px-0 py-0">
                                    <div className="bg-gray-50 border-t border-b">
                                      {previewLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                                          <span className="text-sm text-gray-600">Loading preview...</span>
                                        </div>
                                      ) : previewData?.error ? (
                                        <div className="flex items-center justify-center py-6 text-red-500 text-sm">
                                          <AlertCircle className="h-4 w-4 mr-2" />
                                          {previewData.error}
                                        </div>
                                      ) : previewData ? (
                                        <div className="p-4">
                                          <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center space-x-3">
                                              <Table className="h-4 w-4 text-blue-600" />
                                              <span className="text-sm font-medium text-gray-900">
                                                Sample Data — Showing {previewData.preview_rows} of {(previewData.total_rows || 0).toLocaleString()} rows
                                              </span>
                                            </div>
                                            <button
                                              onClick={() => { setPreviewFileId(null); setPreviewData(null); }}
                                              className="text-gray-400 hover:text-gray-600"
                                            >
                                              <X className="h-4 w-4" />
                                            </button>
                                          </div>
                                          <div className="overflow-x-auto rounded-lg border bg-white">
                                            <table className="min-w-full text-xs">
                                              <thead className="bg-blue-50">
                                                <tr>
                                                  <th className="px-3 py-2 text-left font-semibold text-blue-800 border-r">#</th>
                                                  {previewData.columns.map((col, idx) => (
                                                    <th key={idx} className="px-3 py-2 text-left font-semibold text-blue-800 border-r whitespace-nowrap">
                                                      {col}
                                                    </th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-100">
                                                {previewData.records.map((record, rowIdx) => (
                                                  <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                    <td className="px-3 py-1.5 text-gray-400 border-r font-mono">{rowIdx + 1}</td>
                                                    {previewData.columns.map((col, colIdx) => (
                                                      <td key={colIdx} className="px-3 py-1.5 text-gray-700 border-r whitespace-nowrap max-w-xs truncate" title={String(record[col] ?? '')}>
                                                        {record[col] === null || record[col] === undefined ? (
                                                          <span className="text-gray-300 italic">null</span>
                                                        ) : (
                                                          String(record[col])
                                                        )}
                                                      </td>
                                                    ))}
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedFilesTab;